import {flatMap, isNil, uniq, uniqBy} from 'es-toolkit'
import {isNumber} from 'es-toolkit/compat'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {closeModal} from '../../redux/features/modal'

import getCloudinaryUrl from '../../libs/cloudinary'
import {formatDensity, formatMillimeters, formatOptionalRange} from '../../libs/formatter'

import {MaterialId, ModalMeta} from '../../types'

import infoIcon from '../../../../commons/src/assets/icons/info.svg'

import ContentModal from '../../../../commons/src/components/content-modal/content-modal'
import DescriptionList from '../../../../commons/src/components/description-list'
import DescriptionListItem from '../../../../commons/src/components/description-list/DescriptionListItem'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import ImageContainer from '../../../../commons/src/components/image-container'
import List from '../../../../commons/src/components/list'
import MaterialImage from '../../../../commons/src/components/material-image'
import MaterialListEnumeration from '../../../../commons/src/components/material-list-enumeration/material-list-enumeration'
import Newlines from '../../../../commons/src/components/newlines/Newlines'
import Paragraph from '../../../../commons/src/components/paragraph'
import Stack from '../../../../commons/src/components/stack'
import Tag from '../../../../commons/src/components/tag'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'
import TruncatedListWithCount from '../../../../commons/src/components/truncated-list-with-count'

import DetailViewPartial from '../detail-view-partial'

export const warpingRiskLabelMap: Record<string, string> = {
  high: 'High',
  low: 'Low',
  'N/A': 'N/A',
  over_140_mm: 'Yes, for parts over 140mm or parts with thin long structures',
}

const technicalPropertiesMap: {
  [key: string]: {label: string; formatValue: (value: number | string | boolean) => string}
} = {
  defaultLayerHeight: {
    label: 'Default layer height',
    formatValue: value => `${value} mm`,
  },
  defaultInfill: {
    label: 'Default infill',
    formatValue: value => `${value}%`,
  },
  embossingMin: {
    label: 'Embossing',
    formatValue: value => `min. ${value} mm`,
  },
  engravingMin: {
    label: 'Engraving',
    formatValue: value => `min. ${value} mm`,
  },
  interlockingParts: {
    label: 'Interlocking parts accepted',
    formatValue: value => (value ? 'Yes' : 'No'),
  },
  warpingRisk: {
    label: 'Warping risk',
    formatValue: value =>
      warpingRiskLabelMap[value as keyof typeof warpingRiskLabelMap] || warpingRiskLabelMap['N/A'],
  },
}

const technicalMinMaxPropertiesMap: {
  [key: string]: {label: string; formatValue: (value: number | string) => string}
} = {
  tensileStrength: {
    label: 'Tensile strength',
    formatValue: value => `${value} MPa`,
  },
  tensileModulus: {
    label: 'Tensile modulus',
    formatValue: value => `${value} MPa`,
  },
  tensileElongation: {
    label: 'Tensile elongation',
    formatValue: value => `${value}%`,
  },
  flexuralStrength: {
    label: 'Flexural strength',
    formatValue: value => `${value} MPa`,
  },
  flexuralModulus: {
    label: 'Flexural modulus',
    formatValue: value => `${value} MPa`,
  },
  heatDeflectionTemp66PSI: {
    label: 'HDT (0.45 MPa)',
    formatValue: value => `${value}°C`,
  },
  heatDeflectionTemp264PSI: {
    label: 'HDT (1.8 MPa)',
    formatValue: value => `${value}°C`,
  },
}

type Props = {
  meta: ModalMeta
  materialId: MaterialId
}

const MaterialModal = ({meta, materialId}: Props) => {
  const dispatch = useAppDispatch()
  const material = useAppSelector(state => state.core.materials[materialId])
  const finishGroups = useAppSelector(state => state.core.finishGroups)
  const materialConfigs = useAppSelector(state => state.core.materialConfigs)
  const colors = useAppSelector(state => state.core.colors)

  const finishGroupsForMaterial = Object.values(finishGroups).filter(
    finishGroup => finishGroup.materialId === materialId
  )

  const colorsForMaterial = uniqBy(
    flatMap(finishGroupsForMaterial, finishGroup =>
      Object.values(materialConfigs).filter(
        materialConfig => materialConfig.finishGroupId === finishGroup.id
      )
    ).map(materialConfig => colors[materialConfig.colorId]),
    color => color.id
  )

  const technicalMinMaxProperties = Object.keys(technicalMinMaxPropertiesMap)
    .map(key => ({
      key,
      min: material[`${key}Min` as keyof typeof material] as string | number,
      max: material[`${key}Max` as keyof typeof material] as string | number | null | undefined,
      ...technicalMinMaxPropertiesMap[key],
    }))
    .filter(property => !isNil(property.min))

  const technicalProperties = Object.keys(technicalPropertiesMap)
    .map(key => ({
      key,
      value: material[key as keyof typeof material] as string | number | boolean | null | undefined,
      ...technicalPropertiesMap[key],
    }))
    .filter(property => !isNil(property.value))

  const minWallThicknessValues = uniq(
    material.printingMethods.map(method => method.minWallThickness).filter(isNumber)
  )
  const hasMultipleWallThicknessValues = minWallThicknessValues.length > 1
  const minWallThickness =
    minWallThicknessValues.length > 0
      ? `${formatOptionalRange(
          Math.min(...minWallThicknessValues),
          Math.max(...minWallThicknessValues)
        )} mm`
      : ''

  return (
    <ContentModal fullscreenMobile={meta.fullscreenMobile} onClose={() => dispatch(closeModal())}>
      <DetailViewPartial
        title={
          <Stack>
            <Headline label={material.name} className="u-no-margin-bottom" size="xl" />
            <TruncatedListWithCount space="l">
              {material.printingMethods.map(printingMethod => (
                <Tag key={printingMethod.id} size="l">
                  {printingMethod.name}
                </Tag>
              ))}
              <Paragraph className="u-no-margin-bottom">
                <Newlines text={material.descriptionShort} />
              </Paragraph>
            </TruncatedListWithCount>
          </Stack>
        }
        image={
          <ImageContainer
            source={getCloudinaryUrl(material.featuredImage, ['w_550'])}
            alt="Image of material"
          />
        }
        content={
          <Stack space="300">
            <Stack>
              <Headline label="Material details" size="s" className="u-no-margin-bottom" />
              <Paragraph className="u-no-margin-bottom">
                <Newlines text={material.description} />
              </Paragraph>
            </Stack>
            <Stack>
              <Headline label="Technical Details" size="s" />
              <DescriptionList alternateRows>
                <DescriptionListItem label="Accuracy">
                  {material.accuracy
                    ? `±${material.accuracy}%${material.accuracyLowerLimit ? ` (lower limit of ±${formatMillimeters(material.accuracyLowerLimit)})` : ''}`
                    : '-'}
                </DescriptionListItem>
                <DescriptionListItem label="Density">
                  {material.density ? formatDensity(material.density) : '-'}
                </DescriptionListItem>
                <DescriptionListItem label="Min. wall thickness">
                  {minWallThickness ? (
                    <span>
                      {minWallThickness}{' '}
                      {hasMultipleWallThicknessValues && (
                        <TooltipHandler
                          tooltip={
                            <Tooltip
                              footer={
                                <List>
                                  {material.printingMethods.map(method => (
                                    <li key={method.id}>{`${method.name}: ${
                                      method.minWallThickness
                                        ? formatMillimeters(method.minWallThickness)
                                        : '-'
                                    }`}</li>
                                  ))}
                                </List>
                              }
                            >
                              Min. wall thickness
                            </Tooltip>
                          }
                        >
                          <Icon source={infoIcon} />
                        </TooltipHandler>
                      )}
                    </span>
                  ) : (
                    '-'
                  )}
                </DescriptionListItem>
              </DescriptionList>
            </Stack>
            {(technicalMinMaxProperties.length > 0 || technicalProperties.length > 0) && (
              <Stack>
                <Headline label="Technical Properties" size="s" className="u-no-margin-bottom" />
                <DescriptionList alternateRows>
                  {technicalMinMaxProperties.map(technicalProperty => (
                    <DescriptionListItem
                      key={technicalProperty.key}
                      label={technicalProperty.label}
                    >
                      {technicalProperty.formatValue(
                        formatOptionalRange(technicalProperty.min, technicalProperty.max)
                      )}
                    </DescriptionListItem>
                  ))}
                  {technicalProperties.map(technicalProperty => (
                    <DescriptionListItem
                      key={technicalProperty.key}
                      label={technicalProperty.label}
                    >
                      {technicalProperty.value
                        ? technicalProperty.formatValue(technicalProperty.value)
                        : '-'}
                    </DescriptionListItem>
                  ))}
                </DescriptionList>
              </Stack>
            )}
          </Stack>
        }
        sidebar={
          <>
            <Headline label="Finishes" size="s" />
            <MaterialListEnumeration moreLabel="more">
              {finishGroupsForMaterial.map(finishGroup => finishGroup.name)}
            </MaterialListEnumeration>
            <Headline label="Colors" size="s" className="u-margin-top-l" />
            <TruncatedListWithCount maxLength={7} space="l">
              {colorsForMaterial.map(color => (
                <MaterialImage
                  key={color.id}
                  border
                  color={color.hexCode}
                  image={color.image && getCloudinaryUrl(color.image, ['w_10', 'h_10', 'c_fill'])}
                />
              ))}
            </TruncatedListWithCount>
            {material.tags.length > 0 && (
              <>
                <Headline label="Properties" size="s" className="u-margin-top-l" />
                <MaterialListEnumeration moreLabel="more">
                  {material.tags.map(tag => tag.name)}
                </MaterialListEnumeration>
              </>
            )}
          </>
        }
      />
    </ContentModal>
  )
}

export default MaterialModal
